import { useCallback, useRef, useState } from 'react';
import { SidebarMode } from './types';

const TRANSITION_DURATION = 200;
const VELOCITY_THRESHOLD = 0.5;
const POSITION_THRESHOLD = 0.5;
const STANDARD_SIDEBAR_WIDTH = 430;

type Props = {
  mode: SidebarMode;
  setMode: (mode: SidebarMode) => void;
  containerRef: React.RefObject<HTMLDivElement>;
};

type DragState = 'idle' | 'dragging';

export function useDragAnimation({ mode, setMode, containerRef }: Props) {
  const [dragState, setDragState] = useState<DragState>('idle');
  const [shouldTransition, setShouldTransition] = useState(false);
  const [mainBgWidth, setMainBgWidth] = useState<string>('');
  const [sidebarBgWidth, setSidebarBgWidth] = useState<string>('');
  const [mainPaneWidth, setMainPaneWidth] = useState<string>('');
  const [sidebarPaneWidth, setSidebarPaneWidth] = useState<string>('');

  const dragStartX = useRef<number>(0);
  const dragStartTime = useRef<number>(0);
  const lastMoveX = useRef<number>(0);
  const lastMoveTime = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!containerRef.current) return;

      dragStartX.current = e.clientX;
      dragStartTime.current = Date.now();
      lastMoveX.current = e.clientX;
      lastMoveTime.current = Date.now();

      document.body.style.userSelect = 'none';

      const containerWidth = containerRef.current.offsetWidth;
      const dragHandleWidth = 4;
      const gapWidth = 4;
      const totalNonPaneWidth = dragHandleWidth + gapWidth;
      const availableWidth = containerWidth - totalNonPaneWidth;

      setDragState('dragging');

      const expandedWidth = availableWidth * 0.5;
      const standardMainWidth = availableWidth - STANDARD_SIDEBAR_WIDTH;

      if (mode === 'standard') {
        setMainBgWidth(`${standardMainWidth}px`);
        setSidebarBgWidth(`${STANDARD_SIDEBAR_WIDTH}px`);
        setSidebarPaneWidth(`${STANDARD_SIDEBAR_WIDTH}px`);
        setMainPaneWidth('');

        requestAnimationFrame(() => {
          setShouldTransition(true);
          setMainBgWidth('50%');
          setSidebarBgWidth('50%');

          setTimeout(() => {
            setShouldTransition(false);
          }, TRANSITION_DURATION);
        });
      } else {
        setMainBgWidth(`${expandedWidth}px`);
        setSidebarBgWidth(`${expandedWidth}px`);
        setMainPaneWidth(`${expandedWidth}px`);
        setSidebarPaneWidth('');

        requestAnimationFrame(() => {
          setShouldTransition(true);
          setMainBgWidth(`${standardMainWidth}px`);
          setSidebarBgWidth(`${STANDARD_SIDEBAR_WIDTH}px`);

          setTimeout(() => {
            setShouldTransition(false);
          }, TRANSITION_DURATION);
        });
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!containerRef.current) return;

        lastMoveX.current = moveEvent.clientX;
        lastMoveTime.current = Date.now();

        const containerWidth = containerRef.current.offsetWidth;
        const dragHandleWidth = 4;
        const gapWidth = 4;
        const totalNonPaneWidth = dragHandleWidth + gapWidth;
        const availableWidth = containerWidth - totalNonPaneWidth;
        const expandedWidth = availableWidth * 0.5;

        const deltaX = dragStartX.current - moveEvent.clientX;

        if (mode === 'standard') {
          const newSidebarWidth = Math.max(
            STANDARD_SIDEBAR_WIDTH,
            Math.min(expandedWidth, STANDARD_SIDEBAR_WIDTH + deltaX),
          );
          setSidebarPaneWidth(`${newSidebarWidth}px`);
        } else {
          const newMainWidth = Math.max(
            expandedWidth,
            Math.min(availableWidth - STANDARD_SIDEBAR_WIDTH, expandedWidth - deltaX),
          );
          setMainPaneWidth(`${newMainWidth}px`);
        }
      };

      const handleMouseUp = () => {
        if (!containerRef.current) return;

        document.body.style.userSelect = '';

        const containerWidth = containerRef.current.offsetWidth;
        const dragHandleWidth = 4;
        const gapWidth = 4;
        const totalNonPaneWidth = dragHandleWidth + gapWidth;
        const availableWidth = containerWidth - totalNonPaneWidth;

        const deltaX = dragStartX.current - lastMoveX.current;
        const deltaTime = lastMoveTime.current - dragStartTime.current;
        const velocity = deltaTime > 0 ? deltaX / deltaTime : 0;

        const expandedWidth = availableWidth * 0.5;
        const travelDistance = expandedWidth - STANDARD_SIDEBAR_WIDTH;
        const currentDistance = Math.abs(deltaX);
        const positionRatio = currentDistance / travelDistance;

        const shouldCommit =
          Math.abs(velocity) > VELOCITY_THRESHOLD || positionRatio > POSITION_THRESHOLD;

        setShouldTransition(true);

        if (mode === 'standard' && deltaX > 0 && shouldCommit) {
          setMode('expanded');
          setSidebarPaneWidth('');
        } else if (mode === 'expanded' && deltaX < 0 && shouldCommit) {
          setMode('standard');
          setMainPaneWidth('');
        } else {
          if (mode === 'standard') {
            setSidebarPaneWidth(`${STANDARD_SIDEBAR_WIDTH}px`);
          } else {
            setMainPaneWidth('');
          }
        }

        setDragState('idle');
        setMainBgWidth('');
        setSidebarBgWidth('');

        setTimeout(() => {
          setShouldTransition(false);
          setMainPaneWidth('');
          setSidebarPaneWidth('');
        }, TRANSITION_DURATION);

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [mode, setMode, containerRef],
  );

  return {
    handleMouseDown,
    dragState,
    shouldTransition,
    mainBgWidth,
    sidebarBgWidth,
    mainPaneWidth,
    sidebarPaneWidth,
  };
}
